'use client';
import {
  FC,
  Fragment,
  useMemo,
  useState,
  useEffect,
  JSX,
  useCallback,
} from 'react';

import Modal, { ModalPropTypes } from '@/layouts/Modal';
import { sleep } from '@/utils/helper';
import { MagnifyingGlassIcon } from '@/icons';
import { Svg, Text } from '@/components';
import { useLocation } from '@/context/locationContext';
import { getGoogleReverseGeocode, updateAddressCookie } from '@/app/actions';
import { RadioGroup, Radio } from '@headlessui/react';
import { AddressUserDetails } from '@/models/user-details';
import { useUser } from '@/context/userContext';
import { twMerge } from 'tailwind-merge';
import AddressCard from '@/components/AddressCard';
import { storage } from '@/utils/storage';
import { DEFAULT_STOCK_ID, DEFAULT_STORE_CODE } from '@/constants';
import { useCart } from '@/context/cartContext';
import { logger } from '@/config';
import Image from 'next/image';
import LocationSvg from '../../../../public/images/global/locationSvg.svg';
import { useCleverTap } from '@/context/clevertapContext';
import LocationIcon from '@/icons/LocationIcon';

type PropTypes = ModalPropTypes & {
  redirectTo?: string;
  asGuest?: boolean;
  message?: string;
};

const LocationFlow: FC<PropTypes> = ({ show, onClose }): JSX.Element => {
  const {
    geolocationOptions,
    getMagentoStoreData,
    setAddress,
    setCoordinates,
    setLocationToast,
    setShowSearchManually,
    setIsDelivery,
    setHeaderLocationTitle,
    setStoreCode,
    setAddressString,
  } = useLocation();
  const { fetchUpdateCart } = useCart();
  const { pushEvent } = useCleverTap();
  const { userDetails } = useUser();

  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(false);
  const [selected, setSelected] = useState<AddressUserDetails | null>(null);

  const addressesExist = useMemo(() => {
    if (userDetails?.addresses && userDetails?.addresses.length > 0) {
      return true;
    }
    return false;
  }, [userDetails]);

  const defaultShippingBillingAddress: AddressUserDetails | null =
    useMemo(() => {
      if (userDetails?.default_shipping) {
        return userDetails?.addresses?.find(
          (item: AddressUserDetails) =>
            Number(userDetails?.default_shipping) === item.id
        );
      } else {
        return null;
      }
    }, [userDetails?.addresses, userDetails?.default_shipping]);

  const addresses = userDetails?.addresses?.sort(
    ({ id }: AddressUserDetails) =>
      defaultShippingBillingAddress?.id === id ? -1 : 0
  );

  const handleOpenSearchManually = useCallback(() => {
    setShowSearchManually(true);
    pushEvent({
      event: 'Website Location Prompt Clicked',
      data: {
        'Page URL': window.location.href,
        'Page Name': document.title,
        'Button Clicked': 'Search Manually',
      },
    });
    onClose();
  }, [pushEvent, setShowSearchManually]);

  const onLocationModalClose = useCallback(async () => {
    onClose();
    await sleep(1000);
    setLocationError(null); // Clear any previous error
  }, [onClose]);

  const handleSelected = useCallback(
    async (e: AddressUserDetails) => {
      setSelected(e);
      const latitude =
        e?.custom_attributes?.[0]?.value || storage.get('lat') || '';
      const longitude =
        e?.custom_attributes?.[1]?.value || storage.get('lng') || '';
      setCoordinates({ lat: parseFloat(latitude), lng: parseFloat(longitude) });
      const dataString = await getGoogleReverseGeocode(
        parseFloat(latitude),
        parseFloat(longitude),
        1
      );
      const address =
        e?.street[0] +
        ', ' +
        (e?.street[1] ? e?.street[1] + ', ' : '') +
        ', ' +
        e?.city +
        ', ' +
        e?.region.region;
      if (dataString) {
        const { data, cartKeys } = JSON.parse(dataString);
        const storeCode =
          data?.store_code || data?.default_store_code || DEFAULT_STORE_CODE;
        if (data?.store_code === '') {
          storage.set('serviceable', 'false');
          setAddress(address);
          updateAddressCookie(address, address);
        } else {
          storage.set('serviceable', 'true');
          storage.set('selectedAddressId', e?.id.toString());
          setAddress(address);
          updateAddressCookie(address, address);
        }
        storage.set(
          'default_store_code',
          data?.default_store_code || DEFAULT_STORE_CODE
        );
        const titleText = e?.street[0]?.split(',')[0] || e?.street[0];
        storage.set('addressTitle', titleText);
        storage.set('address', titleText);
        setHeaderLocationTitle(titleText);
        storage.set('addressDescription', address);
        storage.set('store_code', storeCode);
        setStoreCode(storeCode);
        storage.set('stock_id', data?.stock_id || DEFAULT_STOCK_ID);
        storage.set('lat', latitude);
        storage.set('lng', longitude);
        storage.set('wizzy_api_key', data?.wizzy_api_key);
        storage.set('wizzy_store_id', data?.wizzy_store_id);
        storage.set('addressString', address);
        setAddressString(address);
        onLocationModalClose();
        const serviceable = storage.get('serviceable');
        if (cartKeys) {
          storage.set(cartKeys?.key, cartKeys?.value);
          fetchUpdateCart();
        }
        if (serviceable === 'true') {
          setLocationToast({
            message:
              "You're in our service area! Delivery typically takes 3-4 hours",
            deliveryType: 'home',
          });
        }

        setTimeout(() => setLocationToast(undefined), 2000);
      }
    },
    [
      fetchUpdateCart,
      getGoogleReverseGeocode,
      onLocationModalClose,
      setAddress,
      setAddressString,
      setCoordinates,
      setHeaderLocationTitle,
      setLocationToast,
      setStoreCode,
      updateAddressCookie,
    ]
  );

  const handleLocationAccess = useCallback(() => {
    setIsLoadingLocation(true);
    pushEvent({
      event: 'Website Location Prompt Clicked',
      data: {
        'Page URL': window.location.href,
        'Page Name': document.title,
        'Button Clicked': 'Current Location',
      },
    });
    setLocationError(null);
    storage.set('selectedAddressId', '');
    setSelected(null);

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser.');
      setIsLoadingLocation(false);
      return;
    }

    navigator.permissions
      .query({ name: 'geolocation' })
      .then(permissionStatus => {
        if (permissionStatus.state === 'denied') {
          pushEvent({
            event: 'GPS Location Permission Query',
            data: {
              'Page URL': window.location.href,
              'Page Name': document.title,
              Permission: 'Denied',
              'Prompt Source': 'Location Flow Dialog',
            },
          });
          setLocationError(
            'Location access is blocked. Please enable it in your browser settings.'
          );
          setIsLoadingLocation(false);
          return;
        }
        navigator.geolocation.getCurrentPosition(
          async position => {
            try {
              pushEvent({
                event: 'GPS Location Permission Query',
                data: {
                  'Page URL': window.location.href,
                  'Page Name': document.title,
                  Permission: 'Granted',
                  Position: `${position.coords.latitude}, ${position.coords.longitude}`,
                  Accuracy: position.coords.accuracy,
                  'Prompt Source': 'Location Flow Dialog',
                },
              });
              await getMagentoStoreData({
                coords: {
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                  accuracy: position.coords.accuracy,
                  intentionalClick: 0,
                },
              });
              pushEvent({
                event: 'GPS Location Fetched',
                data: {
                  'Page URL': window.location.href,
                  'Page Name': document.title,
                  'Store Code': storage.get('store_code') || DEFAULT_STORE_CODE,
                  Serviceable: storage.get('serviceable') || false,
                  'Prompt Source': 'Location Flow Dialog',
                },
              });
              onLocationModalClose();
            } catch (error) {
              setLocationError(
                'Unable to get location data. Please try again or enter manually.'
              );
              logger.error('Error getting store data:', error);
            } finally {
              setIsLoadingLocation(false);
            }
          },
          error => {
            let errorMessage = 'Unable to retrieve location.';
            if (error.code === 1) {
              errorMessage =
                'Please allow location access and try again or search location manually.';
            }
            if (error.code === 2) errorMessage = 'Position unavailable.';
            if (error.code === 3) errorMessage = 'Location request timed out.';
            setLocationError(errorMessage);
            setIsLoadingLocation(false);
          },
          geolocationOptions
        );
      })
      .catch(() => {
        setLocationError('Unable to check location permissions.');
        setIsLoadingLocation(false);
      });
  }, [getMagentoStoreData, onLocationModalClose, pushEvent]);

  useEffect(() => {
    setIsDelivery(false);
    storage.set('isDelivery', 'false');
    const cachedAddressId = storage.get('selectedAddressId');
    if (cachedAddressId && addresses) {
      const selectedAddressId = addresses?.find(
        (addr: { id: number }) => addr.id === Number(cachedAddressId) || null
      );
      setSelected(selectedAddressId);
    }
  }, [addresses]);

  useEffect(() => {
    if (addressesExist && !storage.get('selectedAddressId') && addresses) {
      const firstAddress = addresses[0];
      setSelected(firstAddress);
      storage.set('selectedAddressId', String(firstAddress.id));
    }
  }, [addressesExist, addresses]);

  useEffect(() => {
    if (show) {
      pushEvent({
        event: 'Website Location Prompt Shown',
        data: {
          'Page URL': window.location.href,
          'Page Name': document.title,
        },
      });
    }
  }, [show, pushEvent]);

  return (
    <>
      <Modal
        show={show}
        showCloseButton={false}
        closeOnOutsideClick={false}
        onClose={onClose}
        id='location-flow'
        style='transition-all duration-300 rounded-t-3xl rounded-b-none'
        preventScroll={true}>
        <div className='flex flex-col items-center justify-center gap-y-4 max-lg:w-full'>
          <Image src={LocationSvg} alt='Location' />
          <Text className='-mt-4 px-12 py-0 text-center text-xl font-semibold'>
            Your Nearest Stores, Just a Tap Away!
          </Text>
          <div className='flex items-center justify-center'>
            <Text className='text-sm text-[#707072] md:text-base'>
              Enter your delivery location to explore gourmet <br />
              favourites near you.
            </Text>
          </div>
          <div className='flex w-full flex-col gap-3'>
            <button
              type='button'
              className='flex w-full cursor-pointer flex-row items-center justify-center rounded-[3.75rem] bg-fig'
              onClick={handleOpenSearchManually}>
              <Svg className='h-6 w-6 stroke-white font-normal'>
                <MagnifyingGlassIcon />
              </Svg>
              <Text className='px-1 py-[10px] text-sm md:text-base font-semibold text-white'>
                SEARCH LOCATION MANUALLY
              </Text>
            </button>
            <button
              type='button'
              onClick={handleLocationAccess}
              disabled={isLoadingLocation}
              className={`flex w-full cursor-pointer flex-row items-center justify-center rounded-[3.75rem] bg-white border border-[#cccccd] ${
                isLoadingLocation ? 'opacity-70' : ''
              }`}>
              <Svg className='h-4 w-4 stroke-[#A0383F]'>
                <LocationIcon />
              </Svg>
              <Text className='px-[6px] py-[10px] text-sm md:text-base font-semibold text-[#38373A]'>
                {isLoadingLocation
                  ? 'GETTING LOCATION...'
                  : 'USE MY CURRENT LOCATION'}
              </Text>
            </button>
            {locationError && (
              <Text className='text-center text-xs text-fig'>
                {locationError}
              </Text>
            )}
          </div>

          {/* Saved Addresses - Only show when not searching and input length < 3 */}
          {addressesExist && (
            <RadioGroup
              value={selected ?? undefined}
              onChange={handleSelected}
              className='mt-4 w-full space-y-3 md:hidden'>
              <div className='flex w-full flex-col gap-y-4 px-2 lg:px-0'>
                <Text className='-my-2 ml-[-8px] text-left text-sm font-medium text-black'>
                  Saved Addresses
                </Text>
                {addresses.map((address: AddressUserDetails) => (
                  <Fragment key={address.id}>
                    <Radio
                      value={address}
                      className={({ checked }) =>
                        twMerge(
                          'flex w-full cursor-pointer rounded-lg',
                          checked && 'base-radio-selected border border-fig'
                        )
                      }>
                      {({ checked }: any) => (
                        <div className='w-full'>
                          <AddressCard
                            address={address}
                            layout='Location'
                            checked={checked}
                          />
                        </div>
                      )}
                    </Radio>
                  </Fragment>
                ))}
              </div>
            </RadioGroup>
          )}
        </div>
      </Modal>
    </>
  );
};

export default LocationFlow;
